import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import AIContextAssistModal from '../Shared/AIContextAssistModal';
import { extractAISQLAssistSuggestionText } from '../Shared/AISQLAssistUtils';
import { ManagedDataSetInput } from '../Shared/ManagedDataSet';
import GetAIProviderListModelRequest from '../../Interfaces/Requests/AI/GetAIProviderListModelRequest';
import GetAISingleTurnPromptModelRequest from '../../Interfaces/Requests/AI/GetAISingleTurnPromptModelRequest';
import GetAIProviderListModelResponse from '../../Interfaces/Responses/AI/GetAIProviderListModelResponse';
import GetAISingleTurnPromptModelResponse from '../../Interfaces/Responses/AI/GetAISingleTurnPromptModelResponse';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedAdminEditorMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
 } from '../Shared/ManagedHelpers';
import {
    AddIcon,
    Button,
    Checkbox,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import BulbOutlinedIlluminated from '../Shared/BulbOutlinedIlluminated';

export type ManagedDataSetInputEditResult = {
    operation: ManagedAdminChildOperation;
    dataSetInput: ManagedDataSetInput;
};

type AUDDataSetInputFormValues = {
    dsiTitle: string;
    dsiInputName: string;
    dsiSourceQuery: string;
    dsiMultiSelect: boolean;
    dsiSelector: string;
    dsiDataFormat: string;
    dsiRequired: boolean;
    dsiWhereQuery: string;
    dsiGlobalDesignator: string;
    dsiSort: number;
    dsiParentID: number;
    dsiDefaultValue: string;
};

type AUDDataSetInputsProps = {
    dataSetID: number;
    dataSetName: string;
    dataSetInputs: ManagedDataSetInput[];
    initialSelectedDataSetInputID?: number;
    initialMode?: ManagedAdminActionMode;
    handleSetDataSetInput: (
        result: ManagedDataSetInputEditResult
    ) => void;
    close: () => void;
};

const AUDDataSetInputs: React.FC<AUDDataSetInputsProps> = ({ close, ...props }) => {
    const { user } = useAuth();
    const [formAUDDataSetInput] = Form.useForm();

    const [aiContextAssistModalOpen, setAIContextAssistModalOpen] = useState(false);
    const [aiContextAssistCurrentText, setAIContextAssistCurrentText] = useState('');
    const [aiContextAssistResultText, setAIContextAssistResultText] = useState('');
    const [aiContextAssistAncillaryItems, setAIContextAssistAncillaryItems] = useState<string[]>([]);
    const [aiContextAssistLoading, setAIContextAssistLoading] = useState(false);
    const [aiContextAssistContext, setAIContextAssistContext] = useState({
        fieldName: 'dsiWhereQuery',
        title: 'AI DataSet Where Query Assist',
        currentSectionTitle: 'Current WHERE / Filter',
        requestSectionTitle: 'What do you want this filter to do?',
        resultSectionTitle: 'Suggested WHERE Clause',
        requestPlaceholder: 'Describe the filter you want AI to help build.',
        askButtonText: 'Ask AI',
        useButtonText: 'Use Suggestion',
    });

    const handleOpenAIWhereQueryAssistModal = () => {
        const fieldName = 'dsiWhereQuery';

        setAIContextAssistContext({
            fieldName: fieldName,
            title: 'AI DataSet Where Query Assist',
            currentSectionTitle: 'Current WHERE / Filter',
            requestSectionTitle: 'What do you want this filter to do?',
            resultSectionTitle: 'Suggested WHERE Clause',
            requestPlaceholder: 'Describe the filter you want AI to help build.',
            askButtonText: 'Ask AI',
            useButtonText: 'Use Suggestion',
        });
        setAIContextAssistCurrentText(formAUDDataSetInput.getFieldValue(fieldName) ?? '');
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);
        setAIContextAssistModalOpen(true);
    };

    const handleOpenAISourceQueryAssistModal = () => {
        const fieldName = 'dsiSourceQuery';

        setAIContextAssistContext({
            fieldName: fieldName,
            title: 'AI DataSet Source Query Assist',
            currentSectionTitle: 'Current Source Query',
            requestSectionTitle: 'What do you want this source query to do?',
            resultSectionTitle: 'Suggested Source Query',
            requestPlaceholder: 'Describe the source query you want AI to help build or review.',
            askButtonText: 'Ask AI',
            useButtonText: 'Use Suggestion',
        });
        setAIContextAssistCurrentText(formAUDDataSetInput.getFieldValue(fieldName) ?? '');
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);
        setAIContextAssistModalOpen(true);
    };

    const handleAskAIContextAssist = (requestText: string) => {
        const aiContextAssistProvider = getAIContextAssistProvider();

        if (!aiContextAssistProvider) {
            setAIContextAssistAncillaryItems([
                `Unable to find an AI Provider matching ${getAIContextAssistProviderNamePhrase()}.`,
            ]);
            return;
        }

        setAIContextAssistLoading(true);
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);

        getAIContextAssistPrompt({
            externalVendorID: aiContextAssistProvider.externalVendorID,
            tenantID: null,
            prompt: buildAIContextAssistPrompt(requestText),
        });
    };

    const handleCopyAIContextAssistResult = (resultText: string) => {
        navigator.clipboard.writeText(resultText);
    };

    const handleUseAIContextAssistSuggestion = (resultText: string) => {
        formAUDDataSetInput.setFieldValue(aiContextAssistContext.fieldName, resultText);
        setAIContextAssistModalOpen(false);
    };

    const dataSetInputs = props.dataSetInputs ?? [];

    const getInitialDSIMode = (): ManagedAdminActionMode => {
        switch (props.initialMode) {
            case 'update':
                return 'update';
            case 'delete':
                return 'delete';
            case 'add':
            default:
                return 'add';
        }
    };

    const getInitialSelectedDataSetInput = (): ManagedDataSetInput | undefined => {
        if (!props.initialSelectedDataSetInputID || props.initialSelectedDataSetInputID <= 0) {
            return undefined;
        }

        return dataSetInputs.find(
            (dataSetInput) => dataSetInput.id === props.initialSelectedDataSetInputID
        );
    };

    const initialSelectedDataSetInput = getInitialSelectedDataSetInput();
    const initialDSIMode = getInitialDSIMode();

    const initialDSIFormValues = initialDSIMode === 'add'
        ? {
            dsiTitle: '',
            dsiInputName: '',
            dsiSourceQuery: '',
            dsiMultiSelect: false,
            dsiSelector: 'dropdown',
            dsiDataFormat: 'string',
            dsiRequired: false,
            dsiWhereQuery: '',
            dsiGlobalDesignator: '',
            dsiSort: 0,
            dsiDefaultValue: '',
        }
        : {
            dsiTitle: initialSelectedDataSetInput?.title ?? '',
            dsiInputName: initialSelectedDataSetInput?.inputName ?? '',
            dsiSourceQuery: initialSelectedDataSetInput?.sourceQuery ?? '',
            dsiMultiSelect: initialSelectedDataSetInput?.multiSelect ?? false,
            dsiSelector: initialSelectedDataSetInput?.selector ?? 'dropdown',
            dsiDataFormat: initialSelectedDataSetInput?.dataFormat ?? 'string',
            dsiRequired: initialSelectedDataSetInput?.required ?? false,
            dsiWhereQuery: initialSelectedDataSetInput?.whereQuery ?? '',
            dsiGlobalDesignator: initialSelectedDataSetInput?.globalDesignator ?? '',
            dsiSort: initialSelectedDataSetInput?.sort ?? 0,
            dsiDefaultValue: initialSelectedDataSetInput?.defaultValue ?? '',
        };

    const [addButtonClicked, setAddButtonClicked] = useState(initialDSIMode === 'add');
    const [updButtonClicked, setUpdButtonClicked] = useState(initialDSIMode === 'update');
    const [delButtonClicked, setDelButtonClicked] = useState(initialDSIMode === 'delete');

    const [dsiSelectedDataSetInputID, setDSISelectedDataSetInputID] = useState(initialSelectedDataSetInput?.id ?? -1);
    const [dsiSelectedDataSetInputName, setDSISelectedDataSetInputName] = useState(
        initialSelectedDataSetInput?.title ?? ''
    );

    const [dsiSelector, setDSISelector] = useState<string>(initialSelectedDataSetInput?.selector ?? 'dropdown');
    const [dsiDataFormat, setDSIDataFormat] = useState<string>(initialSelectedDataSetInput?.dataFormat ?? 'string');

    const [dsiSelectedDataSetInputCreatedBy, setDSISelectedDataSetInputCreatedBy] = useState(initialSelectedDataSetInput?.createdBy ?? -1);
    const [dsiSelectedDataSetInputCreatedTs, setDSISelectedDataSetInputCreatedTs] = useState(initialSelectedDataSetInput?.createdTs ?? new Date());

    const dsiPropsDataSetID = props.dataSetID > 0 ? props.dataSetID : 0;
    const dsiPropsDataSetName = props.dataSetName !== '' ? props.dataSetName : 'New DataSet';

    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const { data: aiProviders = [], isLoading: isGetAIProvidersLoading } = useQuery<GetAIProviderListModelResponse[], unknown>(
        ['aud-dataset-input-ai-providers'],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpGet_GetAIProviders`;
                const requestData: GetAIProviderListModelRequest = {};

                return await AxiosUtil.get<GetAIProviderListModelResponse[], GetAIProviderListModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_DATASET_INPUT_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                console.error('AUD_DATASET_INPUT_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                setManagedActionModalState(
                    buildManagedActionModalState(
                        'info',
                        'AI Provider Load Error',
                        'Unable to load AI Providers for DataSet Input assistance.',
                        true
                    )
                );
            },
        }
    );

    const getAIContextAssistProviderNamePhrase = (): string => {
        return aiContextAssistContext.fieldName === 'dsiSourceQuery'
            ? 'SQL Review'
            : 'Filter Assist';
    };

    const getAIContextAssistProvider = (): GetAIProviderListModelResponse | undefined => {
        const providerNamePhrase = getAIContextAssistProviderNamePhrase().toLowerCase();

        return aiProviders.find((provider) =>
            provider.name.toLowerCase().includes(providerNamePhrase) ||
            provider.displayName.toLowerCase().includes(providerNamePhrase)
        );
    };

    const buildAIContextAssistPrompt = (requestText: string): string => {
        const currentSQLText = formAUDDataSetInput.getFieldValue(aiContextAssistContext.fieldName) ?? '';
        const targetTextDescription = aiContextAssistContext.fieldName === 'dsiSourceQuery'
            ? 'Source Query SQL text'
            : 'WHERE / Filter SQL text';

        return [
            'RetainBI SQL assistance request.',
            `Assist area: ${aiContextAssistContext.title}`,
            '',
            'Current SQL/context:',
            currentSQLText,
            '',
            'User request:',
            requestText,
            '',
            'Output instructions:',
            `Return only the clean, drop-in ready ${targetTextDescription}.`,
            'Do not include markdown, explanation, labels, or surrounding commentary in the suggested SQL.',
            'Do not include credentials, connection strings, secrets, PHI, PII, tenant-isolated data, or system security details.',
            'Do not suggest executing SQL. The user must review and apply any suggestion manually.',
        ].join('\n');
    };

    const { mutate: getAIContextAssistPrompt } = useMutation(
        async ({ externalVendorID, tenantID, prompt }: GetAISingleTurnPromptModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpPost_AISingleTurnPrompt`;
                const requestData: GetAISingleTurnPromptModelRequest = {
                    externalVendorID: externalVendorID,
                    tenantID: tenantID,
                    prompt: prompt,
                };

                return await AxiosUtil.post<GetAISingleTurnPromptModelResponse, GetAISingleTurnPromptModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_DATASET_INPUT_AI_CONTEXT_ASSIST: [AI Context Assist] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAISingleTurnPromptModelResponse) => {
                const rawResponseText = response.text ?? '';
                const suggestedSQLText = extractAISQLAssistSuggestionText(rawResponseText);
                const responseWasCleaned = rawResponseText.trim() !== suggestedSQLText.trim();

                setAIContextAssistResultText(suggestedSQLText);
                setAIContextAssistAncillaryItems([
                    response.success ? 'AI suggestion returned successfully.' : 'AI suggestion request did not succeed.',
                    responseWasCleaned ? 'RetainBI removed explanatory text before showing the suggested SQL.' : '',
                    response.message ?? '',
                ].filter((item) => item.trim().length > 0));
                setAIContextAssistLoading(false);
            },
            onError: (err: unknown) => {
                console.error('AUD_DATASET_INPUT_AI_CONTEXT_ASSIST: [AI Context Assist] Internal Error: ', err);
                setAIContextAssistAncillaryItems([
                    'Unable to complete the AI SQL assistance request.',
                ]);
                setAIContextAssistLoading(false);
            },
        }
    );


    const setDSIMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const getDSIOperation = (): ManagedAdminChildOperation => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        return 'delete';
    };

    const getDSIActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const clearDSISelection = () => {
        setDSISelectedDataSetInputID(-1);
        setDSISelectedDataSetInputName('');
        setDSISelectedDataSetInputCreatedBy(0);
        setDSISelectedDataSetInputCreatedTs(new Date());
        setDSIMode('add');
        setDSIBlankForm();
    };

    const getSelectedDataSetInput = (
        selectedDataSetInputID: number = dsiSelectedDataSetInputID
    ): ManagedDataSetInput | undefined => {
        return dataSetInputs.find((dataSetInput) => dataSetInput.id === selectedDataSetInputID);
    };

    const setDSIBlankForm = () => {
        formAUDDataSetInput.setFieldValue('dsiTitle', '');
        formAUDDataSetInput.setFieldValue('dsiInputName', '');
        formAUDDataSetInput.setFieldValue('dsiSourceQuery', '');
        formAUDDataSetInput.setFieldValue('dsiMultiSelect', false);
        formAUDDataSetInput.setFieldValue('dsiSelector', 'dropdown');
        formAUDDataSetInput.setFieldValue('dsiDataFormat', 'string');
        formAUDDataSetInput.setFieldValue('dsiRequired', false);
        formAUDDataSetInput.setFieldValue('dsiWhereQuery', '');
        formAUDDataSetInput.setFieldValue('dsiDefaultValue', '');
        formAUDDataSetInput.setFieldValue('dsiSort', 0);

        setDSISelector('dropdown');
        setDSIDataFormat('string');
    };

    const setDSIFormFromSelectedDataSetInput = () => {
        const selectedDataSetInput = getSelectedDataSetInput();

        formAUDDataSetInput.setFieldValue('dsiTitle', selectedDataSetInput?.title ?? '');
        formAUDDataSetInput.setFieldValue('dsiInputName', selectedDataSetInput?.inputName ?? '');
        formAUDDataSetInput.setFieldValue('dsiSourceQuery', selectedDataSetInput?.sourceQuery ?? '');
        formAUDDataSetInput.setFieldValue('dsiMultiSelect', selectedDataSetInput?.multiSelect ?? false);
        formAUDDataSetInput.setFieldValue('dsiSelector', selectedDataSetInput?.selector ?? 'dropdown');
        formAUDDataSetInput.setFieldValue('dsiDataFormat', selectedDataSetInput?.dataFormat ?? 'string');
        formAUDDataSetInput.setFieldValue('dsiRequired', selectedDataSetInput?.required ?? false);
        formAUDDataSetInput.setFieldValue('dsiWhereQuery', selectedDataSetInput?.whereQuery ?? '');
        formAUDDataSetInput.setFieldValue('dsiDefaultValue', selectedDataSetInput?.defaultValue ?? '');
        formAUDDataSetInput.setFieldValue('dsiSort', selectedDataSetInput?.sort ?? 0);

        setDSISelector(selectedDataSetInput?.selector ?? 'dropdown');
        setDSIDataFormat(selectedDataSetInput?.dataFormat ?? 'string');
    };

    const handleDSIDataSetInputResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setDSIBlankForm();
            return;
        }

        setDSIFormFromSelectedDataSetInput();
    };

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredDSIField = (fieldName: string): Promise<void> => {
        const value = formAUDDataSetInput.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateDSIDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'dsiTitle':
            case 'dsiWhereQuery':
            case 'dsiSort':
                return validateRequiredDSIField(rule.field);

            case 'dsiInputName':
            case 'dsiSourceQuery':
            case 'dsiMultiSelect':
            case 'dsiSelector':
            case 'dsiDataFormat':
            case 'dsiRequired':
            case 'dsiGlobalDesignator':
            case 'dsiDefaultValue':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const handleDSISelectorChange = (value: string) => {
        setDSISelector(value);
    };

    const handleDSIDataFormatChange = (value: string) => {
        setDSIDataFormat(value);
    };

    const buildManagedDataSetInputRequest = (values: AUDDataSetInputFormValues): ManagedDataSetInput => {
        return {
            id: addButtonClicked ? 0 : (updButtonClicked ? dsiSelectedDataSetInputID : (delButtonClicked ? dsiSelectedDataSetInputID : 0)),
            dataSetId: dsiPropsDataSetID,
            title: values.dsiTitle,
            inputName: values.dsiInputName,
            sourceQuery: values.dsiSourceQuery,
            multiSelect: values.dsiMultiSelect,
            selector: values.dsiSelector,
            dataFormat: values.dsiDataFormat,
            required: values.dsiRequired,
            whereQuery: values.dsiWhereQuery,
            createdBy: addButtonClicked ? (user?.id ?? 0) : (updButtonClicked ? dsiSelectedDataSetInputCreatedBy : (delButtonClicked ? dsiSelectedDataSetInputCreatedBy : 0)),
            modifiedBy: addButtonClicked ? (user?.id ?? 0) : (updButtonClicked ? (user?.id ?? 0) : (delButtonClicked ? (user?.id ?? 0) : 0)),
            deletedBy: addButtonClicked ? 0 : (updButtonClicked ? 0 : (delButtonClicked ? (user?.id ?? 0) : 0)),
            createdTs: addButtonClicked ? (new Date()) : (updButtonClicked ? dsiSelectedDataSetInputCreatedTs : (delButtonClicked ? dsiSelectedDataSetInputCreatedTs : undefined)),
            modifiedTs: addButtonClicked ? (new Date()) : (updButtonClicked ? (new Date()) : (delButtonClicked ? (new Date()) : undefined)),
            deletedTs: addButtonClicked ? (undefined) : (updButtonClicked ? (undefined) : (delButtonClicked ? (new Date()) : undefined)),
            globalDesignator: values.dsiGlobalDesignator,
            sort: Number(values.dsiSort ?? 0),
            parentId: null,
            defaultValue: values.dsiDefaultValue,
        };
    };

    const handleCloseManagedActionModal = () => {
        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const onFinishFailed = (errorInfo: ManagedFormErrorInfo<AUDDataSetInputFormValues>) => {
        console.error('AUDDataSetInputs validation failed:', errorInfo);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'DataSet Input Validation',
                'Please correct the highlighted DataSet Input fields and try again.',
                true
            )
        );
    };

    const onFinish = (values: AUDDataSetInputFormValues) => {
        const newManagedDataSetInput = buildManagedDataSetInputRequest(values);

        props.handleSetDataSetInput({
            operation: getDSIOperation(),
            dataSetInput: newManagedDataSetInput,
        });

        close();
    };

    const handleDSISelectionChange = (value: number) => {
        const selectedDataSetInput = getSelectedDataSetInput(value);

        setDSISelectedDataSetInputID(value);
        setDSISelectedDataSetInputName(selectedDataSetInput?.title ?? '');

        setDSISelectedDataSetInputCreatedBy(selectedDataSetInput?.createdBy ?? 0);
        setDSISelectedDataSetInputCreatedTs(selectedDataSetInput?.createdTs ?? new Date());

        setDSIMode('add');
        setDSIBlankForm();
    };

    //=================================================================
    //=================================================================
    
    const handleDSIAddDataSetInput = () => {
        clearDSISelection();
    };

    //=================================================================
    //=================================================================

    const handleDSIUpdDataSetInput = () => {
        setDSIMode('update');
        handleDSIDataSetInputResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleDSIDelDataSetInput = () => {
        setDSIMode('delete');
        handleDSIDataSetInputResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const onDSICancel = () => {
        clearDSISelection();
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '16px',
        paddingRight: '8px',
        paddingLeft: '8px',
        height: '375px'
    };

    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <AIContextAssistModal
                open={aiContextAssistModalOpen}
                title={aiContextAssistContext.title}
                currentSectionTitle={aiContextAssistContext.currentSectionTitle}
                requestSectionTitle={aiContextAssistContext.requestSectionTitle}
                resultSectionTitle={aiContextAssistContext.resultSectionTitle}
                ancillarySectionTitle='Ancillary Information'
                currentText={aiContextAssistCurrentText}
                resultText={aiContextAssistResultText}
                ancillaryItems={aiContextAssistAncillaryItems}
                requestPlaceholder={aiContextAssistContext.requestPlaceholder}
                askButtonText={aiContextAssistContext.askButtonText}
                useButtonText={aiContextAssistContext.useButtonText}
                loading={aiContextAssistLoading || isGetAIProvidersLoading}
                onAskAI={handleAskAIContextAssist}
                onCopy={handleCopyAIContextAssistResult}
                onUseSuggestion={handleUseAIContextAssistSuggestion}
                onClose={() => setAIContextAssistModalOpen(false)}
            />
            <Spin spinning={false}>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage DataSet Inputs</Title>
                    </Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'DataSet: [' + dsiPropsDataSetID + '] ' + dsiPropsDataSetName}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'DataSet Input: [' + dsiSelectedDataSetInputID + '] ' + dsiSelectedDataSetInputName}</Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{float: 'right'}}>Select DataSet Input to Update or Delete:</Text>
                    </Col>
                    <Col span={14}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a DataSet Input'}
                            showSearch
                            style={{paddingTop: '4px', width: '95%'}}
                            onChange={handleDSISelectionChange}
                        >
                            {dataSetInputs.map(
                                (eachDataSetInput) => {
                                    return (
                                        <Option
                                            key={eachDataSetInput.id}
                                            label={eachDataSetInput.title}
                                            value={eachDataSetInput.id}
                                        >
                                            {eachDataSetInput.title}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={5}>
                        <Button
                            icon={<AddIcon />}
                            style={{ float: 'right' , marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDSIAddDataSetInput}
                        >
                            Add DataSet Input
                        </Button>
                    </Col>
                    <Col span={8} />
                    <Col span={5}>
                        <Button
                            disabled={dsiSelectedDataSetInputID === -1}
                            icon={<EditIcon />}
                            style={{ backgroundColor: 'darkgray', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDSIUpdDataSetInput}
                        >
                            Update DataSet Input
                        </Button>
                    </Col>
                    <Col span={1} />
                    <Col span={5}>
                        <Button
                            disabled={dsiSelectedDataSetInputID === -1}
                            icon={<DeleteIcon />}
                            style={{ backgroundColor: 'red', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDSIDelDataSetInput}
                        >
                            Delete DataSet Input
                        </Button>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Form
                    form={formAUDDataSetInput}
                    initialValues={initialDSIFormValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: getDSIActionColor() }}>
                                    {addButtonClicked ? 'Add DataSet Input' :
                                        (updButtonClicked ? 'Update DataSet Input: [' + dsiSelectedDataSetInputID + '] ' :
                                            (delButtonClicked ? 'Delete DataSet Input: [' + dsiSelectedDataSetInputID + '] ' : ''))}
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    disabled={delButtonClicked}
                                    style={{ color: getDSIActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={() => handleDSIDataSetInputResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={7}>
                                <Form.Item
                                    key='dsititle'
                                    name='dsiTitle'
                                    label='Title'
                                    labelCol={{ span: 8, }}
                                    rules={[
                                        {
                                            message: 'Title required.',
                                            required: true,
                                            validator: validateDSIDetailsData,
                                        },
                                    ]}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Title'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={7}>
                                <Form.Item
                                    key='dsiinputname'
                                    label='Input Name'
                                    labelCol={{ span: 8, }}
                                    name='dsiInputName'
                                    rules={[
                                        {
                                            validator: validateDSIDetailsData,
                                        },
                                    ]}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Input Name'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={10}>
                                <Form.Item
                                    key='dsiglobaldesignator'
                                    label='Global Designator'
                                    labelCol={{ span: 8, }}
                                    name='dsiGlobalDesignator'
                                    rules={[
                                        {
                                            validator: validateDSIDetailsData,
                                        },
                                    ]}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Global Designator'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={5}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        key='dsiselector'
                                        name='dsiSelector'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            label='Selector Type:'
                                            placeholder='Select Selector Type'
                                            showSearch
                                            value={dsiSelector}
                                            onChange={handleDSISelectorChange}
                                        >
                                            <Option
                                                value={'between'}
                                                label={'Between'}
                                            >
                                                {'Between'}
                                            </Option>
                                            <Option
                                                value={'datepicker'}
                                                label={'Date Picker'}
                                            >
                                                {'Date Picker'}
                                            </Option>
                                            <Option
                                                value={'dropdown'}
                                                label={'Drop Down'}
                                            >
                                                {'Drop Down'}
                                            </Option>
                                            <Option
                                                value={'numeric'}
                                                label={'Numeric'}
                                            >
                                                {'Numeric'}
                                            </Option>
                                            <Option
                                                value={'text'}
                                                label={'Text'}
                                            >
                                                {'Text'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={5}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        key='dsidataformat'
                                        name='dsiDataFormat'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            label='Data Format:'
                                            placeholder='Select Data Format'
                                            showSearch
                                            value={dsiDataFormat}
                                            onChange={handleDSIDataFormatChange}
                                        >
                                            <Option
                                                value={'DateTime'}
                                                label={'Date Time'}
                                            >
                                                {'Date Time'}
                                            </Option>
                                            <Option
                                                value={'numeric'}
                                                label={'Numeric'}
                                            >
                                                {'Numeric'}
                                            </Option>
                                            <Option
                                                value={'string'}
                                                label={'String'}
                                            >
                                                {'String'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    key='dsisort'
                                    name='dsiSort'
                                    label='Sort'
                                    labelCol={{ span: 8, }}
                                    rules={[
                                        {
                                            message: 'Sort required.',
                                            required: true,
                                            validator: validateDSIDetailsData,
                                        },
                                    ]}
                                    style={{marginBottom: '4px', marginTop: '26px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Sort'
                                        style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '60%'}}
                                        type='number'
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    key='dsimultiselect'
                                    name='dsiMultiSelect'
                                    valuePropName='checked'
                                    style={{marginTop: '26px'}}
                                >
                                    <Checkbox
                                        disabled={delButtonClicked}
                                        style={{float: 'left'}}
                                    >
                                        Multi Select
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    key='dsirequired'
                                    name='dsiRequired'
                                    valuePropName='checked'
                                    style={{marginTop: '26px'}}
                                >
                                    <Checkbox
                                        disabled={delButtonClicked}
                                        style={{float: 'left'}}
                                    >
                                        Required
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={24}>
                                <Form.Item
                                    label='Where Query'
                                    labelCol={{ span: 4, }}
                                    style={{marginBottom: '4px'}}
                                >
                                    <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                                        <Form.Item
                                            key='dsiwherequery'
                                            name='dsiWhereQuery'
                                            rules={[
                                                {
                                                    message: 'Where Query required.',
                                                    required: true,
                                                    validator: validateDSIDetailsData,
                                                },
                                            ]}
                                            style={{ flex: 1, marginBottom: '0px' }}
                                        >
                                            <Input.TextArea
                                                disabled={delButtonClicked}
                                                placeholder='Enter Where Query'
                                                style={{
                                                    color: delButtonClicked ? 'gray' : 'black',
                                                    height: '64px',
                                                    overflowY: 'auto',
                                                    resize: 'none',
                                                    paddingRight: '12px',
                                                    borderRadius: '4px',
                                                }}
                                            />
                                        </Form.Item>
                                        <Button
                                            disabled={delButtonClicked || isGetAIProvidersLoading}
                                            icon={<BulbOutlinedIlluminated />}
                                            style={{
                                                minWidth: '32px',
                                                padding: '0px',
                                            }}
                                            title='AI Assist'
                                            type='default'
                                            onClick={handleOpenAIWhereQueryAssistModal}
                                        />
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={24}>
                                <Form.Item
                                    label='Source Query'
                                    labelCol={{ span: 4, }}
                                    style={{marginBottom: '4px'}}
                                >
                                    <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                                        <Form.Item
                                            key='dsisourcequery'
                                            name='dsiSourceQuery'
                                            rules={[
                                                {
                                                    validator: validateDSIDetailsData,
                                                },
                                            ]}
                                            style={{ flex: 1, marginBottom: '0px' }}
                                        >
                                            <Input.TextArea
                                                disabled={delButtonClicked}
                                                placeholder='Enter Source Query'
                                                style={{
                                                    color: delButtonClicked ? 'gray' : 'black',
                                                    height: '64px',
                                                    overflowY: 'auto',
                                                    resize: 'none',
                                                    paddingRight: '12px',
                                                    borderRadius: '4px',
                                                }}
                                            />
                                        </Form.Item>
                                        <Button
                                            disabled={delButtonClicked || isGetAIProvidersLoading}
                                            icon={<BulbOutlinedIlluminated />}
                                            style={{
                                                minWidth: '32px',
                                                padding: '0px',
                                            }}
                                            title='AI Assist'
                                            type='default'
                                            onClick={handleOpenAISourceQueryAssistModal}
                                        />
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={24}>
                                <Form.Item
                                    key='dsidefaultvalue'
                                    name='dsiDefaultValue'
                                    label='Default Value'
                                    labelCol={{ span: 4, }}
                                    rules={[
                                        {
                                            validator: validateDSIDetailsData,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <div style={{ textAlign: 'left', width: '100%' }}>
                                        <Input
                                            disabled={delButtonClicked}
                                            placeholder='Enter Default Value'
                                            style={{ color: delButtonClicked ? 'gray' : 'black', width: '40%', }}
                                        />
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={5}>
                            <Form.Item
                                name='dsiCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={9} />
                        <Col span={5}>
                            <Form.Item
                                name='dsiApplyButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')), float: 'right', width: '95%' }}
                                    type='primary'
                                >
                                    {addButtonClicked ? 'Add DataSet Input' : (updButtonClicked ? 'Update DataSet Input' : (delButtonClicked ? 'Delete DataSet Input' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item
                                name='dsiCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={onDSICancel}
                                >
                                    {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDDataSetInputs;
